import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Upload,
  ShoppingCart,
  Box,
  DollarSign,
  Brain,
  BarChart3,
  Truck,
  ArrowLeft,
  X,
  Calendar,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-gray-400', path: '/dashboard' },
  { id: 'orders', label: 'Orders', icon: ShoppingCart, color: 'text-gray-400', path: '/orders' },
  { id: 'shipments', label: 'Shipments', icon: Truck, color: 'text-gray-400', path: '/shipments' },
  { id: 'inventory-insights', label: 'Inventory Insights', icon: BarChart3, color: 'text-gray-400', path: '/inventory-insights' },
  { id: 'ai-insights', label: 'AI Insights', icon: Brain, color: 'text-gray-400', path: '/ai-insights', hidden: true },
  { id: 'replenishables', label: 'Replenishables', icon: Box, color: 'text-gray-400', path: '/replenishables' },
  { id: 'returns', label: 'Returns', icon: ArrowLeft, color: 'text-gray-400', path: '/returns' },
  { id: 'inventory-costs', label: 'Inventory Costs', icon: DollarSign, color: 'text-gray-400', path: '/inventory-costs' },
  { id: 'historic-orders', label: 'Historic Orders', icon: Calendar, color: 'text-gray-400', path: '/historic-orders', hidden: true },
  { id: 'data-upload', label: 'Data Upload', icon: Upload, color: 'text-gray-400', path: '/data-upload', hidden: true },
  { id: 'settings', label: 'Settings', icon: Settings, color: 'text-gray-400', path: '/settings' },
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onClose
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleNavigation = (path: string) => {
    navigate(path);
    onClose(); // Close mobile sidebar after navigation
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar - Always collapsed, expands on hover */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-screen bg-gray-800 border-r border-gray-700 outline outline-1 outline-gray-600 transform transition-all duration-300 ease-in-out lg:translate-x-0 overflow-y-auto shadow-lg
          ${isHovered ? 'w-64' : 'w-16'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <div className={`flex items-end gap-2 transition-all ${isHovered ? 'justify-start' : 'justify-center'}`}>
            <div className="w-8 h-8 flex items-end justify-center gap-1 px-1 flex-shrink-0">
              {/* Three bars getting bigger from left to right */}
              <div className="w-1.5 bg-cashy-purple-400 rounded-sm" style={{ height: '14px' }}></div>
              <div className="w-1.5 bg-cashy-purple-500 rounded-sm" style={{ height: '18px' }}></div>
              <div className="w-1.5 bg-cashy-purple-600 rounded-sm" style={{ height: '22px' }}></div>
            </div>
            {isHovered && (
              <h1 className="text-xl font-bold text-white whitespace-nowrap leading-none pb-0.5">cashy.so</h1>
            )}
          </div>
        </div>

        <nav className="p-2">
          <ul className="space-y-1">
            {/* Close button for mobile only */}
            <li className="lg:hidden">
              <div className="flex items-center justify-end p-2">
                <button 
                  onClick={onClose}
                  className="p-2 rounded hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
                  title="Close sidebar"
                >
                  <X size={16} />
                </button>
              </div>
            </li>
            
            {menuItems.filter((item) => !item.hidden).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group
                      ${isActive 
                        ? 'bg-gradient-to-r from-cashy-purple-500/20 to-cashy-purple-600/20 text-white shadow-lg border border-cashy-purple-400/30' 
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                      ${!isHovered ? 'justify-center px-2' : ''}
                    `}
                    title={!isHovered ? item.label : undefined}
                  >
                    <Icon 
                      size={20} 
                      className={`${isActive ? 'text-cashy-purple-400' : item.color} group-hover:scale-110 transition-transform flex-shrink-0`} 
                    />
                    {isHovered && (
                      <span className="font-medium whitespace-nowrap">{item.label}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        
        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cashy-purple-400 via-cashy-purple-500 to-cashy-purple-600"></div>
      </aside>
    </>
  );
};